import { useCallback, useMemo } from 'react';

import { useResource } from 'react-resource-router';

import { type TimezoneOption } from '../../common/ui/timezone-select/types';
import { getTimezonesResource } from '../../services/resources';
import { formatTimezone, guessTimeZone } from '../../utils/time';

import { type GetTimezoneOptionParams } from './types';

const useTimezoneOptions = () => {
	const { data, loading } = useResource(getTimezonesResource);

	const timezones: TimezoneOption[] = useMemo(
		() =>
			data?.timezones
				.map(
					(timezone): TimezoneOption => ({
						value: timezone.id,
						offset: timezone.currentOffset,
						label: formatTimezone({
							name: timezone.id,
							offset: timezone.currentOffset,
							variant: 'short',
						}),
					}),
				)
				?.sort((a, b) => {
					if (a.value === b.value) {
						return a.offset - b.offset;
					}

					return a.value.localeCompare(b.value);
				}) ?? [],
		[data],
	);

	const getTimezoneOption = useCallback(
		({ offset, timeZone }: GetTimezoneOptionParams = {}): TimezoneOption => {
			if (!timezones.length) {
				return {} as TimezoneOption;
			}

			if (timeZone) {
				return timezones.find(({ value }) => value === timeZone)!;
			}

			if (offset) {
				return timezones.find(({ offset: tzOffset }) => tzOffset === offset)!;
			}

			// TODO: use Jira user timezone
			return timezones.find(({ value }) => value === guessTimeZone()) ?? timezones[0];
		},
		[timezones],
	);

	return { timezones, loading, getTimezoneOption };
};

export { useTimezoneOptions };
